/**
 * "How many X per month", "which month had the most check-ins", "are my
 * invoices going up" — for any report count whose endpoint filters by date.
 *
 * None of the report searches can group by month, so each month is its own
 * `size=1` count request, run in parallel and capped like every other fan-out
 * in the assistant. That is what lets one module answer month questions for
 * invoices and gate activity across all three audiences without a bespoke
 * endpoint per report.
 */

import moment, { type Moment } from 'moment';
import type { ComparisonQuestion } from './comparisonQuestion';
import type { ApiDateRange } from './requests/apiDates';
import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
} from './knowledgeRegistry';

/**
 * One count request per month, so a window longer than this keeps its latest
 * months. A year of full months plus the one under way.
 */
export const MAX_MONTH_REQUESTS = 13;

/** Full months counted when the question names no period (plus this month so far). */
const DEFAULT_FULL_MONTHS = 12;

/** Months averaged on each side of the "last 3 vs the 3 before" trend comparison. */
const TREND_SPAN = 3;

const SLASH_DAY = 'MM/DD/YYYY';
const MONTH_KEY = 'YYYY-MM';

export interface MonthlyCountSpec {
  noun: string;
  nounPlural: string;
  supportsLocation: boolean;
  /** Counts the report's rows in one window, optionally for one location. */
  countFor: (range: ApiDateRange, locationId?: string) => Promise<number>;
  /** The report link, opened on the whole window counted when its page takes one. */
  linkFor: (range: ApiDateRange) => KnowledgeLink | undefined;
  /** The window a named period covers, already in the API's MM/DD/YYYY form. */
  periodRange: ApiDateRange;
}

interface MonthSlot {
  key: string;
  range: ApiDateRange;
  /** "(so far)" for the month under way. */
  current: boolean;
  /** Cut short by the window, so it is listed but not ranked. */
  partial: boolean;
}

interface MonthCount extends MonthSlot {
  count: number;
}

const format = (day: Moment): string => day.format(SLASH_DAY);

const monthName = (key: string): string => moment(key, MONTH_KEY, true).format('MMMM YYYY');

/** Every calendar month the window touches, each clipped to the window. */
const monthSlots = (start: Moment, end: Moment, today: Moment): MonthSlot[] => {
  const slots: MonthSlot[] = [];
  for (let month = start.clone().startOf('month'); !month.isAfter(end, 'month'); month.add(1, 'month')) {
    const monthStart = month.clone().startOf('month');
    const monthEnd = month.clone().endOf('month').startOf('day');
    const from = moment.max(monthStart, start.clone().startOf('day'));
    const to = moment.min(monthEnd, end.clone().startOf('day'));
    const current = month.isSame(today, 'month');
    slots.push({
      key: month.format(MONTH_KEY),
      range: { startDate: format(from), endDate: format(to) },
      current,
      partial: current || !from.isSame(monthStart, 'day') || !to.isSame(monthEnd, 'day'),
    });
  }
  return slots;
};

interface CountWindow {
  slots: MonthSlot[];
  label: string;
  /** Months dropped from the start of a window longer than {@link MAX_MONTH_REQUESTS}. */
  dropped: number;
}

const windowFor = (context: KnowledgeContext, spec: MonthlyCountSpec): CountWindow => {
  const today = moment().startOf('day');

  let start: Moment;
  let end: Moment;
  let label: string;
  if (context.period.explicit) {
    start = moment(spec.periodRange.startDate, SLASH_DAY, true);
    end = moment.min(moment(spec.periodRange.endDate, SLASH_DAY, true), today);
    label = context.period.label;
  } else {
    start = today.clone().subtract(DEFAULT_FULL_MONTHS, 'months').startOf('month');
    end = today;
    label = `over the last ${DEFAULT_FULL_MONTHS} months`;
  }

  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return { slots: [], label, dropped: 0 };

  const all = monthSlots(start, end, today);
  const slots = all.slice(-MAX_MONTH_REQUESTS);
  return { slots, label, dropped: all.length - slots.length };
};

const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`;

const average = (values: number[]): number =>
  values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;

/** Whole numbers stay whole; an average of a few items keeps one decimal. */
const formatAverage = (value: number): string =>
  Number.isInteger(value) || value >= 100 ? Math.round(value).toLocaleString('en-US') : value.toFixed(1);

const describeTrend = (complete: MonthCount[], spec: MonthlyCountSpec): string => {
  if (complete.length < TREND_SPAN * 2) {
    return `There are not yet ${TREND_SPAN * 2} full months to show a direction.`;
  }
  const recent = average(complete.slice(-TREND_SPAN).map((month) => month.count));
  const prior = average(complete.slice(-TREND_SPAN * 2, -TREND_SPAN).map((month) => month.count));
  const lead = `The last ${TREND_SPAN} full months averaged **${formatAverage(recent)}** ${spec.nounPlural} a month`;

  if (prior === 0) {
    return recent === 0
      ? `There were no ${spec.nounPlural} in the last ${TREND_SPAN * 2} full months.`
      : `${lead}, up from none in the ${TREND_SPAN} months before.`;
  }
  const change = Math.round(((recent - prior) / prior) * 100);
  if (change === 0) return `${lead}, level with the ${TREND_SPAN} months before.`;
  return (
    `${lead}, ${change > 0 ? 'up' : 'down'} ${Math.abs(change)}% on the ${TREND_SPAN} months ` +
    `before (${formatAverage(prior)} a month).`
  );
};

/**
 * Counts a report month by month and names the busiest and quietest months,
 * the monthly average and — when asked — the direction.
 */
export const answerCountsByMonth = async (
  context: KnowledgeContext,
  spec: MonthlyCountSpec,
  comparison: ComparisonQuestion,
): Promise<KnowledgeAnswer> => {
  const span = windowFor(context, spec);
  const { targets, wholeAccount } = context;

  const oneLocation = spec.supportsLocation && !wholeAccount && targets.length === 1 ? targets[0] : null;
  // Months times locations would be a burst of requests for one answer, so a
  // set of locations is counted as a whole — and the answer says so.
  const widened = spec.supportsLocation && !wholeAccount && targets.length > 1;
  const scope = oneLocation ? ` at **${oneLocation.name}**` : '';

  const notes: string[] = [];
  if (!spec.supportsLocation && !wholeAccount) {
    notes.push('This report cannot be filtered by location, so these figures cover the whole account.');
  }
  if (widened) {
    notes.push(
      `You asked about ${targets.length} locations; counting each of them month by month would ` +
        'take too many lookups, so these figures cover every location. Ask about one location ' +
        'for its own months.',
    );
  }

  if (span.slots.length === 0) {
    return { text: `There is nothing to count ${span.label} yet.` };
  }

  const months: MonthCount[] = await Promise.all(
    span.slots.map(async (slot) => ({ ...slot, count: await spec.countFor(slot.range, oneLocation?.id) })),
  );

  const link = spec.linkFor({
    startDate: span.slots[0].range.startDate,
    endDate: span.slots[span.slots.length - 1].range.endDate,
  });

  if (span.dropped > 0) {
    notes.push(
      `Each month is its own lookup, so I counted the latest ${MAX_MONTH_REQUESTS} months of ` +
        `that window (${monthName(months[0].key)} to ${monthName(months[months.length - 1].key)}).`,
    );
  }

  const total = months.reduce((sum, month) => sum + month.count, 0);
  if (total === 0) {
    return {
      text: [`There were no ${spec.nounPlural}${scope} ${span.label}.`, ...notes].join(' '),
      link,
    };
  }

  const breakdown: KnowledgeBreakdownRow[] = months.map((month) => ({
    label: `${monthName(month.key)}${month.current ? ' (so far)' : month.partial ? ' (part month)' : ''}`,
    value: month.count.toLocaleString('en-US'),
  }));

  // Part months are listed but never ranked: two weeks of one month against
  // two of the next says nothing about which month was busier.
  const complete = months.filter((month) => !month.partial);
  if (complete.length === 0) {
    const text =
      `There ${total === 1 ? 'was' : 'were'} **${pluralize(total, spec.noun, spec.nounPlural)}**${scope} ` +
      `${span.label}, but no full month falls in that window, so there is no month to rank. ` +
      'Here is each part month.';
    return { text: [text, ...notes].join(' '), breakdown, link };
  }

  const busiest = complete.reduce((top, month) => (month.count > top.count ? month : top));
  const quietest = complete.reduce((low, month) => (month.count < low.count ? month : low));
  const describe = (month: MonthCount): string =>
    `**${monthName(month.key)}**, with **${pluralize(month.count, spec.noun, spec.nounPlural)}**`;

  const sentences: string[] = [];
  if (complete.length === 1) {
    sentences.push(`Only one full month${scope} ${span.label} can be compared: ${describe(busiest)}.`);
  } else {
    if (comparison.wantsLowest) {
      sentences.push(
        `The quietest month for ${spec.nounPlural}${scope} ${span.label} was ${describe(quietest)}.`,
        `The busiest was ${describe(busiest)}.`,
      );
    } else {
      sentences.push(
        `The busiest month for ${spec.nounPlural}${scope} ${span.label} was ${describe(busiest)}.`,
        `The quietest was ${describe(quietest)}.`,
      );
    }
    sentences.push(
      `That averages **${formatAverage(average(complete.map((month) => month.count)))}** a month ` +
        `over ${complete.length} full months.`,
    );
  }

  if (comparison.shape === 'trend') sentences.push(describeTrend(complete, spec));
  if (comparison.shape === 'seasonal') {
    sentences.push(
      'Each month is a separate lookup, so this covers one year at most \u2014 read it as this ' +
        'year\u2019s numbers rather than a settled seasonal pattern.',
    );
  }

  return { text: [...sentences, ...notes].join(' '), breakdown, link };
};

export const __testing = { monthSlots };
